
import React, { useEffect } from 'react';
import { Search, CalendarIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { sq } from 'date-fns/locale';
import { useToast } from '@/components/ui/use-toast';

interface SearchFilterBarProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  startDate: Date | undefined;
  endDate: Date | undefined;
  setStartDate: (date: Date | undefined) => void;
  setEndDate: (date: Date | undefined) => void;
  onReset: () => void;
}

const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
  searchTerm,
  setSearchTerm,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  onReset
}) => {
  const { toast } = useToast();

  // Validate date range whenever dates change
  useEffect(() => {
    if (startDate && endDate && startDate > endDate) {
      toast({
        title: "Gabim",
        description: "Data e fillimit duhet të jetë para datës së mbarimit",
        variant: "destructive",
      });
      setEndDate(undefined);
    }
  }, [startDate, endDate, toast, setEndDate]);

  return (
    <div className="mb-4 flex flex-col sm:flex-row items-center gap-2">
      {/* Search input */}
      <div className="w-full sm:w-1/3 relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Kërko sipas titullit..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-8"
        />
      </div>
      
      {/* Date filter - Start date */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="flex justify-start items-center gap-2">
            <CalendarIcon className="h-4 w-4 opacity-70" />
            <span>{startDate ? format(startDate, 'PP', { locale: sq }) : 'Data fillimit'}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={startDate}
            onSelect={setStartDate}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      
      {/* Date filter - End date */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="flex justify-start items-center gap-2">
            <CalendarIcon className="h-4 w-4 opacity-70" />
            <span>{endDate ? format(endDate, 'PP', { locale: sq }) : 'Data mbarimit'}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={endDate}
            onSelect={setEndDate}
            initialFocus
            disabled={(date) => startDate ? date < startDate : false}
          />
        </PopoverContent>
      </Popover>
      
      {/* Reset filters */}
      <Button variant="ghost" onClick={onReset} className="sm:ml-auto">
        Rivendos
      </Button>
    </div>
  );
};

export default SearchFilterBar;
